/**
 * Amount and percentage parsing for offer badges and titles (SEO R3 rule 8, 2026-10-24). Pure.
 *
 * The old parsers read `\$(\d+)`, so "$1,000 back" became "$1" and "$6,000 credit" became "$6". Here an amount keeps its
 * thousands separators ("$1,000" -> 1000) and a "k" suffix ("$5k" -> 5000). A stored FIXED value that is only the first
 * group of the amount in the text (1 for "$1,000") is the same parser bug in the database and is corrected from the text.
 */

export interface MoneyAmount {
    currency: "$" | "€" | "£";
    value: number;
}

// "$1,000", "$ 6,000", "$27", "$4.99", "$5k", "€20", "£10". Thousands groups only in their exact shape (",ddd").
const MONEY = /([$€£])\s?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?(\s?[kK]\b)?/;
const PERCENT = /(\d{1,3}(?:\.\d+)?)\s*%/;

/** The first currency amount in the text, or null. */
export function parseMoney(text: string | null | undefined): MoneyAmount | null {
    if (!text) return null;
    const m = text.match(MONEY);
    if (!m) return null;
    let value = Number(m[2].replace(/,/g, ""));
    if (m[3]) value += Number(`0.${m[3]}`);
    if (m[4]) value *= 1000;
    if (!Number.isFinite(value) || value <= 0) return null;
    return { currency: m[1] as MoneyAmount["currency"], value };
}

/** The first percentage in the text as an integer (1..100), or null. */
export function parsePercent(text: string | null | undefined): number | null {
    if (!text) return null;
    const m = text.match(PERCENT);
    if (!m) return null;
    const value = Math.round(Number(m[1]));
    return value > 0 && value <= 100 ? value : null;
}

/** Every currency amount in the text, in order (same shape rules as parseMoney). */
export function parseMoneyAll(text: string | null | undefined): MoneyAmount[] {
    return parseMoneyMatches(text).map(m => m.amount);
}

/**
 * Every currency amount in the text that the text states as a discount, not as a price: "save $20", "$20 off",
 * "$10 discount". "Plans from $2.99" or "reduced from $69 to $49" state prices, "$5 credit" / "$2 cashback" a rebate:
 * they are left out.
 */
export function parseDiscountAmounts(text: string | null | undefined): MoneyAmount[] {
    if (!text) return [];
    return parseMoneyMatches(text)
        .filter(m => DISCOUNT_BEFORE.test(text.slice(0, m.start)) || DISCOUNT_AFTER.test(text.slice(m.end)))
        .map(m => m.amount);
}

const DISCOUNT_BEFORE = /\b(?:save|saving|savings of|discount of)\s+(?:up to\s+|an extra\s+|extra\s+)?$/i;
const DISCOUNT_AFTER = /^\s*(?:off|discount|savings?)\b/i;

function parseMoneyMatches(text: string | null | undefined): { amount: MoneyAmount; start: number; end: number }[] {
    if (!text) return [];
    const out: { amount: MoneyAmount; start: number; end: number }[] = [];
    for (const m of text.matchAll(new RegExp(MONEY.source, "g"))) {
        const amount = parseMoney(m[0]);
        if (amount) out.push({ amount, start: m.index, end: m.index + m[0].length });
    }
    return out;
}

/** Every distinct percentage in the text, in order, as integers (1..100). "Save 10%, 20% or 30%" -> [10, 20, 30]. */
export function parsePercents(text: string | null | undefined): number[] {
    if (!text) return [];
    const out: number[] = [];
    for (const m of text.matchAll(new RegExp(PERCENT.source, "g"))) {
        const value = parsePercent(m[0]);
        if (value !== null && !out.includes(value)) out.push(value);
    }
    return out;
}

/**
 * Every distinct percentage the text phrases as a saving, in order: "X% off", "X% (extra / recurring / lifetime) discount",
 * "X% savings", "X% cheaper", "X% less", "X% reduction", "save(s) X%", "save up to / over / between / from X%", "savings of X%", "-X%". "20% commissions", "100% SSD", "99.9% uptime",
 * "100% money-back", "20% more storage" are left out.
 */
export function parseDiscountPercents(text: string | null | undefined): number[] {
    if (!text) return [];
    const out: number[] = [];
    for (const m of text.matchAll(new RegExp(PERCENT.source, "g"))) {
        const value = parsePercent(m[0]);
        if (value === null || out.includes(value)) continue;
        const before = text.slice(0, m.index);
        const after = text.slice(m.index + m[0].length);
        if (PERCENT_SAVING_AFTER.test(after) || PERCENT_SAVING_BEFORE.test(before) || PERCENT_MINUS_BEFORE.test(before)) out.push(value);
    }
    return out;
}

// "10% off", "10% extra discount", "25% lifetime discount", "50%+ discount", "now 50% less", "30% reduction",
// "35% price cut". Not "20% more storage", "10% bonus traffic", "7% cashback" (a rebate, not a price cut).
const PERCENT_SAVING_AFTER = /^\+?\s*(?:(?:extra|additional|recurring|lifetime|instant|flat|total|storewide|sitewide|welcome|bonus)\s+)?(?:off|discount(?:ed)?|savings?|cheaper|less|reduction|price cut)\b/i;
const PERCENT_SAVING_BEFORE = /\b(?:save|saves|saving|savings of|discount of)\s+(?:(?:up to|over|about|around|nearly|almost|between|from|an extra|extra|an additional|additional|a flat|flat|an|a)\s+)?$/i;
// "-20%" as a sign, not the dash of a range ("10-20%").
const PERCENT_MINUS_BEFORE = /(?:^|[\s(])[-−–]\s?$/;

/** "$1,000", "€20", "$4.99" (en-US grouping, decimals only when present). */
export function formatMoney(amount: MoneyAmount): string {
    const hasCents = Math.round(amount.value * 100) % 100 !== 0;
    return `${amount.currency}${amount.value.toLocaleString("en-US", { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 })}`;
}

/**
 * A stored FIXED discount, corrected when the offer text shows it was cut at the thousands separator: stored 1 with
 * "$1,000 back" in the text -> 1000. Any other stored value is kept as it is (the verifier may have corrected it).
 */
export function reconcileFixedValue(stored: number | null | undefined, text: string | null | undefined): number | null {
    if (stored == null || !(stored > 0)) return null;
    const m = text ? text.match(MONEY) : null;
    if (m && m[2].includes(",")) {
        const firstGroup = Number(m[2].split(",")[0]);
        const full = Number(m[2].replace(/,/g, ""));
        if (firstGroup === stored && full > stored) return full;
    }
    return stored;
}

/** True when the text states a discount figure (a percentage or an amount). */
export function hasDiscountFigure(text: string | null | undefined): boolean {
    return parsePercent(text) !== null || parseMoney(text) !== null;
}
